import * as Cesium from "cesium";
import { computeZoomLevel } from "../MapUtility";

export const highlightPolylineEndpointsAcrossDataSources = ({ viewer, dataSources, enabled }) => {
  if (!viewer) return;

  const GREEN = Cesium.Color.LIME.withAlpha(0.9);
  const RED = Cesium.Color.RED.withAlpha(0.9);
  const PURPLE = Cesium.Color.PURPLE.withAlpha(0.9);

  const DIST_THRESH_M = 0.001; // 1 mm
  const HEIGHT_TOL_M = 0.1; // 10 cm
  const BUCKET_DEG = 1e-5; // ~1.1 m lat bucket for coarse prune

  // disable: clear all and reset state
  if (!enabled) {
    if (viewer.__endpointHighlightJob) {
      viewer.__endpointHighlightJob.cancelled = true;
      viewer.__endpointHighlightJob = undefined;
    }
    if (viewer.__endpointHighlightDS) {
      viewer.__endpointHighlightDS.entities.removeAll();
    }
    if (viewer.__endpointHighlightState) {
      viewer.__endpointHighlightState.map.clear();
      viewer.__endpointHighlightState.gen = 0;
    }
    viewer.__endpointHighlightPixelSize = undefined;

    if (viewer.__endpointViewRectRetryTid) {
      clearTimeout(viewer.__endpointViewRectRetryTid);
      viewer.__endpointViewRectRetryTid = undefined;
    }
    if (viewer.__endpointHighlightRefreshTid) {
      clearTimeout(viewer.__endpointHighlightRefreshTid);
      viewer.__endpointHighlightRefreshTid = undefined;
    }
    if (viewer.__endpointHighlightCameraChangedHandler) {
      viewer.camera?.changed?.removeEventListener(viewer.__endpointHighlightCameraChangedHandler);
      viewer.__endpointHighlightCameraChangedHandler = undefined;
    }
    if (viewer.__endpointHighlightPrevPercentageChanged != null && viewer.camera) {
      viewer.camera.percentageChanged = viewer.__endpointHighlightPrevPercentageChanged;
      viewer.__endpointHighlightPrevPercentageChanged = undefined;
    }
    viewer.__endpointHighlightEnabled = false;
    viewer.__endpointHighlightDataSources = undefined;
    viewer.__endpointViewRectRetryCount = 0;

    return;
  }

  if (!dataSources?.length) return;

  viewer.__endpointHighlightEnabled = true;
  viewer.__endpointHighlightDataSources = dataSources;

  if (viewer.camera && viewer.__endpointHighlightPrevPercentageChanged == null) {
    viewer.__endpointHighlightPrevPercentageChanged = viewer.camera.percentageChanged;
    viewer.camera.percentageChanged = Math.min(viewer.camera.percentageChanged ?? 0.5, 0.01);
  }

  if (!viewer.__endpointHighlightScheduleRefresh) {
    viewer.__endpointHighlightScheduleRefresh = () => {
      if (!viewer.__endpointHighlightEnabled) return;

      if (viewer.__endpointHighlightRefreshTid) {
        clearTimeout(viewer.__endpointHighlightRefreshTid);
      }

      viewer.__endpointHighlightRefreshTid = setTimeout(() => {
        viewer.__endpointHighlightRefreshTid = undefined;

        if (!viewer.__endpointHighlightEnabled) return;

        highlightPolylineEndpointsAcrossDataSources({
          viewer,
          dataSources: viewer.__endpointHighlightDataSources,
          enabled: true,
        });
      }, 60);
    };
  }

  if (!viewer.__endpointHighlightCameraChangedHandler) {
    viewer.__endpointHighlightCameraChangedHandler = () => {
      viewer.__endpointHighlightScheduleRefresh?.();
    };
    viewer.camera?.changed?.addEventListener(viewer.__endpointHighlightCameraChangedHandler);
  }

  // create / reuse highlight data source
  if (!viewer.__endpointHighlightDS) {
    viewer.__endpointHighlightDS = new Cesium.CustomDataSource("__endpointHighlights");
    viewer.dataSources.add(viewer.__endpointHighlightDS);
  }
  const highlightDS = viewer.__endpointHighlightDS;

  // persistent state for marker reuse
  if (!viewer.__endpointHighlightState) {
    viewer.__endpointHighlightState = { gen: 0, map: new Map() };
  }
  const state = viewer.__endpointHighlightState;
  state.gen++;

  const clearAllMarkers = () => {
    if (viewer.__endpointHighlightJob) {
      viewer.__endpointHighlightJob.cancelled = true;
      viewer.__endpointHighlightJob = undefined;
    }
    if (viewer.__endpointHighlightDS) {
      viewer.__endpointHighlightDS.entities.removeAll();
    }
    if (viewer.__endpointHighlightState) {
      viewer.__endpointHighlightState.map.clear();
    }
    if (viewer.__endpointViewRectRetryTid) {
      clearTimeout(viewer.__endpointViewRectRetryTid);
      viewer.__endpointViewRectRetryTid = undefined;
    }
    viewer.__endpointViewRectRetryCount = 0;
  };

  const zoomLevel = computeZoomLevel(viewer);
  if (zoomLevel != null && zoomLevel <= 16) {
    clearAllMarkers();
    return;
  }

  const now = viewer?.clock?.currentTime ?? Cesium.JulianDate.now();

  // ---------- helpers ----------

  const getShow = (maybeProp) => {
    if (maybeProp == null) return true;
    try {
      return typeof maybeProp.getValue === "function" ? !!maybeProp.getValue(now) : !!maybeProp;
    } catch {
      return true;
    }
  };

  const isEntityVisible = (entity, ds) => {
    if (ds?.show === false) return false;
    let e = entity;
    while (e) {
      if (!getShow(e.show)) return false;
      e = e.parent;
    }
    if (entity.polyline && !getShow(entity.polyline.show)) return false;
    if (typeof entity.isAvailable === "function" && !entity.isAvailable(now)) return false;
    return true;
  };

  const getViewRectangle = (viewer) => {
    const scene = viewer?.scene;
    if (!scene) return undefined;

    const ellipsoid = scene.globe?.ellipsoid;

    try {
      if (ellipsoid) {
        const rect = viewer.camera.computeViewRectangle(ellipsoid);
        if (rect) return rect;
      }
    } catch {
      // ignore
    }

    const canvas = scene.canvas;
    if (!canvas) return undefined;

    const w = Math.max(canvas.clientWidth || canvas.width || 0, 0);
    const h = Math.max(canvas.clientHeight || canvas.height || 0, 0);
    if (w < 2 || h < 2) return undefined;

    const corners = [
      { x: 0, y: 0 },
      { x: w - 1, y: 0 },
      { x: w - 1, y: h - 1 },
      { x: 0, y: h - 1 },
    ];

    const cartographics = [];
    for (const c of corners) {
      const ray = viewer.camera.getPickRay(c);
      if (!ray) continue;
      let pos = scene.globe.pick(ray, scene);
      if (!pos && ellipsoid && typeof viewer.camera.pickEllipsoid === "function") {
        pos = viewer.camera.pickEllipsoid(new Cesium.Cartesian2(c.x, c.y), ellipsoid);
      }
      if (!pos) continue;
      cartographics.push(Cesium.Cartographic.fromCartesian(pos));
    }

    // fallback: at least grab the center point
    if (cartographics.length === 0) {
      try {
        const centerRay = viewer.camera.getPickRay({ x: w / 2, y: h / 2 });
        if (centerRay) {
          let centerPos = scene.globe.pick(centerRay, scene);
          if (!centerPos && ellipsoid && typeof viewer.camera.pickEllipsoid === "function") {
            centerPos = viewer.camera.pickEllipsoid(new Cesium.Cartesian2(w * 0.5, h * 0.5), ellipsoid);
          }
          if (centerPos) cartographics.push(Cesium.Cartographic.fromCartesian(centerPos));
        }
      } catch {
        // ignore
      }
    }

    if (cartographics.length === 0) return undefined;

    // If only ONE point was picked (usually center), build a padded rectangle.
    if (cartographics.length === 1) {
      const c = cartographics[0];
      const z = computeZoomLevel(viewer);
      const padDeg = z != null && z > 20 ? 0.01 : z != null && z > 18 ? 0.05 : z != null && z > 16 ? 0.2 : 1.0;

      const pad = Cesium.Math.toRadians(padDeg);

      const south = Cesium.Math.clamp(c.latitude - pad, -Math.PI / 2, Math.PI / 2);
      const north = Cesium.Math.clamp(c.latitude + pad, -Math.PI / 2, Math.PI / 2);
      const west = c.longitude - pad;
      const east = c.longitude + pad;

      return new Cesium.Rectangle(west, south, east, north);
    }

    let west = Number.POSITIVE_INFINITY;
    let east = Number.NEGATIVE_INFINITY;
    let south = Number.POSITIVE_INFINITY;
    let north = Number.NEGATIVE_INFINITY;

    for (const cg of cartographics) {
      let lon = cg.longitude;
      if (lon > Math.PI) lon -= 2 * Math.PI;
      if (lon < -Math.PI) lon += 2 * Math.PI;
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, cg.latitude);
      north = Math.max(north, cg.latitude);
    }

    return new Cesium.Rectangle(west, south, east, north);
  };

  // cache last valid view rectangle to avoid "process everything" when rect can't be computed
  if (!viewer.__endpointLastViewRect) viewer.__endpointLastViewRect = undefined;
  const viewRectNow = getViewRectangle(viewer);
  if (viewRectNow) viewer.__endpointLastViewRect = viewRectNow;
  const viewRect = viewer.__endpointLastViewRect;

  // If we still can't compute any view rectangle, don't clear existing markers.
  // Retry a few times because Cesium can temporarily fail to pick during render/terrain updates.
  if (!viewRect) {
    const maxRetries = 6;
    viewer.__endpointViewRectRetryCount = (viewer.__endpointViewRectRetryCount || 0) + 1;

    if (viewer.__endpointViewRectRetryCount <= maxRetries && !viewer.__endpointViewRectRetryTid) {
      viewer.__endpointViewRectRetryTid = setTimeout(() => {
        viewer.__endpointViewRectRetryTid = undefined;
        highlightPolylineEndpointsAcrossDataSources({ viewer, dataSources, enabled: true });
      }, 80);
    }
    return;
  }
  viewer.__endpointViewRectRetryCount = 0;

  const inViewRect = (cartesian, rect) => {
    if (!rect) return false;
    const c = Cesium.Cartographic.fromCartesian(cartesian);
    return Cesium.Rectangle.contains(rect, c);
  };

  const surfaceDistanceMeters = (p1, p2) => {
    const c1 = Cesium.Cartographic.fromCartesian(p1);
    const c2 = Cesium.Cartographic.fromCartesian(p2);
    c1.height = 0;
    c2.height = 0;
    const g = new Cesium.EllipsoidGeodesic(c1, c2);
    return g.surfaceDistance || 0;
  };

  const bucketKeyFromCartesian = (pos) => {
    const c = Cesium.Cartographic.fromCartesian(pos);
    const latDeg = Cesium.Math.toDegrees(c.latitude);
    const lonDeg = Cesium.Math.toDegrees(c.longitude);
    const bx = Math.round(lonDeg / BUCKET_DEG);
    const by = Math.round(latDeg / BUCKET_DEG);
    return `${bx},${by}`;
  };

  const neighborBucketKeys = (pos) => {
    const c = Cesium.Cartographic.fromCartesian(pos);
    const latDeg = Cesium.Math.toDegrees(c.latitude);
    const lonDeg = Cesium.Math.toDegrees(c.longitude);
    const bx = Math.round(lonDeg / BUCKET_DEG);
    const by = Math.round(latDeg / BUCKET_DEG);
    const keys = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        keys.push(`${bx + dx},${by + dy}`);
      }
    }
    return keys;
  };

  const idFromCartesian = (pos) => {
    const c = Cesium.Cartographic.fromCartesian(pos);
    const lon = Cesium.Math.toDegrees(c.longitude).toFixed(12);
    const lat = Cesium.Math.toDegrees(c.latitude).toFixed(12);
    return `${lon},${lat}`;
  };

  const colorFromStatus = (status) => {
    if (status === "green") return GREEN;
    if (status === "purple") return PURPLE;
    return RED;
  };

  if (!viewer.__endpointHighlightPixelSize) {
    const cache = { lastMs: 0, lastSize: 8 };
    viewer.__endpointHighlightPixelSize = new Cesium.CallbackProperty(() => {
      const ms = Date.now();
      if (ms - cache.lastMs > 100) {
        const z = computeZoomLevel(viewer);
        cache.lastSize = z != null && z > 20 ? 12 : 8;
        cache.lastMs = ms;
      }
      return cache.lastSize;
    }, false);
  }

  const markerPixelSize = viewer.__endpointHighlightPixelSize;

  const upsertMarker = (id, position, status) => {
    const z = computeZoomLevel(viewer);
    if (z != null && z <= 16) return;

    let marker = state.map.get(id);

    if (!marker) {
      marker = highlightDS.entities.add({
        position,
        point: {
          pixelSize: markerPixelSize,
          color: colorFromStatus(status),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1,
        },
        show: true,
      });
      state.map.set(id, marker);
    } else {
      marker.position = position;
      marker.point.color = colorFromStatus(status);
      marker.point.pixelSize = markerPixelSize;
      marker.show = true;
    }

    marker.__gen = state.gen;
  };

  // ---------- chunked job ----------

  if (viewer.__endpointHighlightJob) viewer.__endpointHighlightJob.cancelled = true;
  const job = { gen: state.gen, cancelled: false };
  viewer.__endpointHighlightJob = job;

  const timeNow = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
  const yieldToBrowser = () =>
    new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => resolve());
      else setTimeout(resolve, 0);
    });

  const maybeYield = async (t0, budgetMs = 12) => {
    const z = computeZoomLevel(viewer);
    if (z != null && z <= 16) {
      clearAllMarkers();
      return false;
    }

    if (job.cancelled || viewer.__endpointHighlightJob !== job || state.gen !== job.gen) return false;
    if (timeNow() - t0 < budgetMs) return true;

    await yieldToBrowser();

    const zAfterYield = computeZoomLevel(viewer);
    if (zAfterYield != null && zAfterYield <= 16) {
      clearAllMarkers();
      return false;
    }

    return !(job.cancelled || viewer.__endpointHighlightJob !== job || state.gen !== job.gen);
  };

  (async () => {
    // collect visible endpoints in view (chunked)
    const endpoints = [];
    let t0 = timeNow();

    for (const ds of dataSources) {
      const ents = ds?.entities?.values;
      if (!ents) continue;

      for (const ent of ents) {
        if (!ent?.polyline) continue;
        if (!isEntityVisible(ent, ds)) continue;

        const positions = ent.polyline.positions?.getValue(now);
        if (!positions || positions.length < 2) continue;

        const a = positions[0];
        const b = positions[positions.length - 1];

        if (inViewRect(a, viewRect)) endpoints.push({ position: a, entity: ent });
        if (inViewRect(b, viewRect)) endpoints.push({ position: b, entity: ent });

        if (!(await maybeYield(t0))) return;
        t0 = timeNow();
      }
    }

    // build 2D buckets
    const buckets = new Map();
    t0 = timeNow();
    for (let i = 0; i < endpoints.length; i++) {
      const k = bucketKeyFromCartesian(endpoints[i].position);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(i);

      if ((i & 2047) === 0) {
        if (!(await maybeYield(t0))) return;
        t0 = timeNow();
      }
    }

    // classify
    const status = new Array(endpoints.length).fill("red");
    t0 = timeNow();

    for (let i = 0; i < endpoints.length; i++) {
      const p = endpoints[i].position;
      const h1 = Cesium.Cartographic.fromCartesian(p).height || 0;

      let hasClose = false;
      let hasMismatch = false;

      const nkeys = neighborBucketKeys(p);
      for (const k of nkeys) {
        const idxs = buckets.get(k);
        if (!idxs) continue;

        for (const j of idxs) {
          if (j === i) continue;

          const d = surfaceDistanceMeters(p, endpoints[j].position);
          if (d >= DIST_THRESH_M) continue;

          hasClose = true;

          const h2 = Cesium.Cartographic.fromCartesian(endpoints[j].position).height || 0;
          const hdiff = Math.abs(h1 - h2);
          if (hdiff > HEIGHT_TOL_M) {
            hasMismatch = true;
            break;
          }
        }

        if (hasMismatch) break;
      }

      status[i] = !hasClose ? "red" : hasMismatch ? "purple" : "green";

      if (!(await maybeYield(t0))) return;
      t0 = timeNow();
    }

    // upsert markers
    t0 = timeNow();
    for (let i = 0; i < endpoints.length; i++) {
      const pos = endpoints[i].position;
      const id = idFromCartesian(pos);
      upsertMarker(id, pos, status[i]);

      if ((i & 2047) === 0) {
        if (!(await maybeYield(t0))) return;
        t0 = timeNow();
      }
    }

    // delete stale markers
    const toDelete = [];
    for (const [id, ent] of state.map) {
      if (ent.__gen !== state.gen) toDelete.push(id);
    }

    t0 = timeNow();
    for (let i = 0; i < toDelete.length; i++) {
      const id = toDelete[i];
      const ent = state.map.get(id);
      if (ent) highlightDS.entities.remove(ent);
      state.map.delete(id);

      if ((i & 2047) === 0) {
        if (!(await maybeYield(t0))) return;
        t0 = timeNow();
      }
    }
  })();
};
